import { Box, Flex, Image, Link, Text } from '@chakra-ui/react';
import { useState } from 'react';
import Page from './Page';
import { FormProvider, useForm } from 'react-hook-form';
import surveysService from '../api/services/surveysService';
import { i18n, parseErrorToString, toaster } from '@shared';
import { getTranslation } from '../utils/translations';
import { useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import SurveyLanguageSelector from '../components/SurveyLanguageSelector';

type SurveyValue = { surveyElementID: number; surveyElementOptionID: number } | { surveyElementID: number; valueText: string };

export default function SurveyContainer({ survey }: { survey: Survey }) {
	const pageCount = getSurveyPageCount();
	const methods = useForm();
	const { token } = useParams<{ identifier: string; token: string }>();
	const { t } = useTranslation();

	const surveyHasBeforeParagraph =
		getTranslation(survey.translations, 'surveys.beforeParagraph', 'fi', false) !== '' ||
		getTranslation(survey.translations, 'surveys.beforeParagraph', 'en', false) !== '' ||
		getTranslation(survey.translations, 'surveys.beforeParagraph', 'sv', false) !== '';

	const surveyHasTitle =
		getTranslation(survey.translations, 'surveys.title', 'fi', false) !== '' ||
		getTranslation(survey.translations, 'surveys.title', 'en', false) !== '' ||
		getTranslation(survey.translations, 'surveys.title', 'sv', false) !== '';
	const { bannerImage } = survey;
	const surveyHasBannerImage = bannerImage[0] && bannerImage[0]?.fileUrl;
	const hasStartPage = surveyHasBeforeParagraph && surveyHasTitle;

	const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
	const [page, setPage] = useState<number>(hasStartPage ? -1 : 0);

	function getSurveyPageCount(): number {
		const pageBreaks = survey.surveyElements?.filter(element => element.elementType === 'pageBreak').length;

		if (pageBreaks) {
			return pageBreaks + (survey.isSummaryPageShown ? 2 : 1);
		}

		return survey.isSummaryPageShown ? 2 : 1;
	}

	function parseSubmissionValues(values: Record<string, string | string[]>) {
		return Object.entries(values)
			.flatMap(([key, value]): SurveyValue | SurveyValue[] | undefined => {
				const element = survey.surveyElements?.find(e => e.surveyElementID === parseInt(key));

				if (element?.elementType === 'number') {
					return {
						surveyElementID: parseInt(key),
						valueText: value.toString(),
					};
				}
				if (element?.elementType === 'checkbox' && Array.isArray(value)) {
					return value.map((optionID: string) => ({
						surveyElementID: parseInt(key),
						surveyElementOptionID: parseInt(optionID),
					}));
				}

				if (value !== undefined && value !== null && value !== '') {
					return {
						surveyElementID: parseInt(key),
						valueText: value as string,
					};
				}

				return undefined;
			})
			.filter((v): v is SurveyValue => v !== undefined); // filter out empties
	}

	async function submitSurvey(values: Record<string, string>) {
		setIsSubmitting(true);
		try {
			const payload = [
				{
					surveySubmissionValues: parseSubmissionValues(values),
					language: i18n.resolvedLanguage as Lang,
				},
			];

			if (payload[0]?.surveySubmissionValues.length === 0) {
				toaster.create({
					type: 'error',
					title: t('general.fill_at_least_one_field'),
				});
				return;
			}

			await surveysService.saveSubmission({
				surveyIdentifier: survey.surveyIdentifier,
				contactToken: token as string,
				surveySubmissions: payload,
			});
			setPage(page + 1);
		} catch (error: unknown) {
			toaster.create({
				type: 'error',
				title: parseErrorToString(error),
			});
		} finally {
			setIsSubmitting(false);
		}
	}

	return (
		<FormProvider {...methods}>
			<form
				className="tav-survey-form"
				onKeyDown={e => {
					if (e.key === 'Enter') {
						e.preventDefault();
					}
				}}
				onSubmit={methods.handleSubmit(submitSurvey)}
				style={{ width: '100%', display: 'flex', justifyContent: 'center' }}
			>
				<Flex className="tav-survey-container" width="100%" backgroundColor="white" flexDirection={'column'} padding={5} height="100%" maxWidth="800px">
					{survey.languages.length > 1 ? (
						<Flex justifyContent={'flex-end'}>
							<SurveyLanguageSelector />
						</Flex>
					) : null}

					{surveyHasBannerImage ? (
						<Image
							className="tav-survey-logo"
							maxWidth="90%"
							maxHeight={'150px'}
							objectFit="contain"
							marginTop="20px"
							marginBottom="30px"
							marginRight="auto"
							marginLeft="auto"
							src={bannerImage[0]?.fileUrl}
						/>
					) : null}
					<Page isSubmitting={isSubmitting} survey={survey} currentPage={page} setPage={setPage} />
					<ProgressBar page={page} pageCount={pageCount} survey={survey} />
					<PoweredBy />
				</Flex>
			</form>
		</FormProvider>
	);
}

function PoweredBy() {
	return (
		<Flex justifyContent="center" marginTop={4} paddingBottom={2} className="tav-survey-powered-by">
			<Text>
				Powered by{' '}
				<Link textDecor="underline" href="https://tavoittaja.fi" target="_blank" color="rgb(61, 213, 152">
					Tavoittaja
				</Link>
			</Text>
		</Flex>
	);
}

function ProgressBar({ page, pageCount, survey }: { page: number; pageCount: number; survey: Survey }) {
	const progress = Math.min(page / pageCount, 1);

	return (
		<Flex marginTop={4} backgroundColor="#EEEEEE" height="13px" minHeight={'13px'} overflow={'hidden'} borderRadius="8px">
			<Box width={`${progress * 100}%`} backgroundColor={survey.colorScheme} transition="width 0.3s ease" />
		</Flex>
	);
}
